import { useEffect, useState, lazy, Suspense } from 'react'
import { useLocation } from 'react-router'

/**
 * Defers mounting of non-critical UI chrome until after the page has had
 * a chance to settle. Mount triggers (whichever comes first):
 *   1. requestIdleCallback fires (or 2.5s timeout fallback)
 *   2. user interacts (scroll, pointer, key)
 *
 * Why: root.tsx used to mount ~10 third-party / decorative components on
 * every page (CrispChat, NewsletterPopup, PushPrompt, BackToTop, etc.).
 * That's ~200 KB of JS hydrating before the user has even scrolled — hurts
 * LCP/TBT and battery on mobile. None of these need to be visible at FCP.
 *
 * Each child is also lazy-imported so the bundles are code-split and only
 * fetched when this gate opens.
 */

const CookieBanner = lazy(() => import('~/components/cookie-banner').then(m => ({ default: m.CookieBanner })))
const PushPrompt = lazy(() => import('~/components/push-prompt').then(m => ({ default: m.PushPrompt })))
const WhatsAppButton = lazy(() => import('~/components/whatsapp-button').then(m => ({ default: m.WhatsAppButton })))
const CrispChat = lazy(() => import('~/components/crisp-chat').then(m => ({ default: m.CrispChat })))
const BackToTop = lazy(() => import('~/components/back-to-top').then(m => ({ default: m.BackToTop })))
const NewsletterPopup = lazy(() => import('~/components/newsletter-popup').then(m => ({ default: m.NewsletterPopup })))
const PwaInstallBanner = lazy(() => import('~/components/pwa-install').then(m => ({ default: m.PwaInstallBanner })))
const KeyboardShortcuts = lazy(() => import('~/components/keyboard-shortcuts').then(m => ({ default: m.KeyboardShortcuts })))
const StickyMobileCta = lazy(() => import('~/components/sticky-mobile-cta').then(m => ({ default: m.StickyMobileCta })))

interface Props {
  crispId?: string
}

export function DeferredOverlays({ crispId }: Props) {
  const [ready, setReady] = useState(false)
  const location = useLocation()
  // CRO: suppress every distraction overlay inside the /demande-devis funnel.
  // Newsletter popup intercepts clicks on quiz tiles; WhatsApp/PushPrompt/PWA
  // banners pull users out of the conversion path. Cookie banner stays (RGPD).
  const inFunnel = location.pathname.startsWith('/demande-devis')

  // RGPD: Crisp is a third-party tracker — load only with analytics consent.
  // Read once when overlays mount (a consent grant reloads the page, so this
  // re-evaluates fresh).
  const [analyticsConsent, setAnalyticsConsent] = useState(false)
  useEffect(() => {
    try {
      const c = JSON.parse(localStorage.getItem('cookie-consent') || '{}')
      setAnalyticsConsent(c && c.analytics === true)
    } catch { /* no consent */ }
  }, [])

  useEffect(() => {
    if (ready) return
    let cancelled = false
    const fire = () => { if (!cancelled) setReady(true) }

    // Idle / fallback timer
    type IdleWin = Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
      cancelIdleCallback?: (id: number) => void
    }
    const w = window as IdleWin
    const idleId: number = w.requestIdleCallback
      ? w.requestIdleCallback(fire, { timeout: 2500 })
      : window.setTimeout(fire, 2500)

    // First interaction also triggers — captures the case where the user
    // engages immediately and we want overlays available right away.
    const interactionEvents: Array<keyof WindowEventMap> = ['scroll', 'pointerdown', 'keydown', 'touchstart']
    const onInteract = () => fire()
    interactionEvents.forEach((ev) => window.addEventListener(ev, onInteract, { once: true, passive: true }))

    return () => {
      cancelled = true
      if (w.cancelIdleCallback) {
        w.cancelIdleCallback(idleId)
      } else {
        clearTimeout(idleId)
      }
      interactionEvents.forEach((ev) => window.removeEventListener(ev, onInteract))
    }
  }, [ready])

  if (!ready) return null

  return (
    <Suspense fallback={null}>
      <KeyboardShortcuts />
      <StickyMobileCta />
      <BackToTop />
      <CookieBanner />
      {!inFunnel && <PushPrompt />}
      {!inFunnel && <WhatsAppButton />}
      {!inFunnel && analyticsConsent && <CrispChat websiteId={crispId} />}
      {!inFunnel && <NewsletterPopup />}
      {!inFunnel && <PwaInstallBanner />}
    </Suspense>
  )
}
